import React, { useState, useEffect } from 'react';
import { Button, TextField, Grid, styled, Icon, CircularProgress, Card, MenuItem, Menu, InputAdornment, IconButton, Typography } from '@mui/material';
import { Formik, Form, Field } from 'formik';
import CloseIcon from '@mui/icons-material/Close';
import * as Yup from 'yup';
import { Helmet } from 'react-helmet-async';
import { Clear } from '@mui/icons-material';
import UploadIcon from '@mui/icons-material/Upload';
import SnackbarComponent from '../../../utils/SnackbarComponent';
import { createCredential, getAllIssuers } from '../../../services/apiservice';
import { useApiData } from '../../../hooks/useApi';

const RedAsterisk = styled('span')({
  color: 'red',
});

const FileInputWrapper = styled('label')({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  width: '100%',
});

const UploadInput = styled('input')({
  display: 'none',
});

const UploadText = styled('span')(({ theme }) => ({
  marginTop: theme.spacing(1),
  color: theme.palette.text.primary,
}));

const CustomMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    maxHeight: 250,
    width: 'calc(37% - 25px)',
    marginTop: theme.spacing(1),
  },
}));

const ImageNameWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

function CredentialForm() {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [issuers, setIssuers] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [imageName, setImageName] = useState(null);
  const { data: fetchedIssuers } = useApiData(() => getAllIssuers().then((res) => res.data), []);

  useEffect(() => {
    if (fetchedIssuers) {
      setIssuers(fetchedIssuers);
    }
  }, [fetchedIssuers]);

  const handleClearImage = (setFieldValue) => {
    setImageName(null);
    setFieldValue('CredentialImageURL', null);
  };

  const validationSchema = Yup.object({
    CredentialName: Yup.string().required('Credential Name is required'),
    CredentialDescription: Yup.string().required('Credential Description is required'),
    CredentialCriteria: Yup.string().required('Credential Criteria is required'),
    CredentialCategory: Yup.string().required('Credential Category is required'),
    CredentialImageURL: Yup.mixed()
      .required('Credential Logo is required')
      .test(
        'fileSize',
        'Image size is too large, max size is 2MB',
        value => !value || (value && value.size <= 2 * 1024 * 1024)
      )
      .test(
        'fileType',
        'Only image formats are allowed (jpg, png, jpeg)',
        value => !value || (value && ['image/jpg', 'image/jpeg', 'image/png'].includes(value.type))
      ),
    // CredentialGroupName: Yup.string().nullable(),
    CredentialIssuerID: Yup.string().required('Issuer is required'),
  });

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const clearIssuerSelection = (setFieldValue) => {
    setFieldValue('CredentialIssuerID', '');
  };
  return (
    <>
      <Helmet>
        <title>Credential Form</title>
      </Helmet>
      <SnackbarComponent
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
        style={{ marginTop: '50px' }}
      />

      <Formik
        initialValues={{
          CredentialName: '',
          CredentialDescription: '',
          CredentialCriteria: '',
          CredentialCategory: '',
          CredentialImageURL: '',
          // CredentialGroupName: '',
          CredentialIssuerID: '',
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          try {
            const formData = new FormData();

            Object.keys(values).forEach(key => {
              if (key !== 'CredentialImageURL') {
                formData.append(key, values[key]);
              }
            });

            if (values.CredentialImageURL) {
              formData.append('CredentialImageURL', values.CredentialImageURL);
            }

            const response = await createCredential(formData);

            if (response.success) {
              setSnackbarMessage('Credential created successfully!');
              setSnackbarSeverity('success');
              resetForm();
              setImageName(null);
            } else {
              setSnackbarMessage('Failed to create credential.');
              setSnackbarSeverity('error');
            }

            setSnackbarOpen(true);
          } catch (error) {
            console.error('Submission error:', error);
            const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
            setSnackbarMessage(errorMessage);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ errors, touched, setFieldValue, setFieldError, isSubmitting, values }) => (
          <div>
            <Card sx={{ width: '100%', padding: 2 }}>
              <Form>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      label={
                        <>
                          {'Credential Name'} <RedAsterisk>*</RedAsterisk>
                        </>
                      }
                      name="CredentialName"
                      fullWidth
                      error={touched.CredentialName && Boolean(errors.CredentialName)}
                      helperText={touched.CredentialName && errors.CredentialName}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      label={
                        <>
                          {'Credential Description'} <RedAsterisk>*</RedAsterisk>
                        </>
                      }
                      name="CredentialDescription"
                      fullWidth
                      error={touched.CredentialDescription && Boolean(errors.CredentialDescription)}
                      helperText={touched.CredentialDescription && errors.CredentialDescription}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      as={TextField}
                      label={
                        <>
                          {'Credential Criteria'} <RedAsterisk>*</RedAsterisk>
                        </>
                      }
                      name="CredentialCriteria"
                      fullWidth
                      error={touched.CredentialCriteria && Boolean(errors.CredentialCriteria)}
                      helperText={touched.CredentialCriteria && errors.CredentialCriteria}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      as={TextField}
                      label={
                        <>
                          {'Credential Category'} <RedAsterisk>*</RedAsterisk>
                        </>
                      }
                      name="CredentialCategory"
                      fullWidth
                      error={touched.CredentialCategory && Boolean(errors.CredentialCategory)}
                      helperText={touched.CredentialCategory && errors.CredentialCategory}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      as={TextField}
                      label={
                        <>
                          {'Credential IssuerID'} <RedAsterisk>*</RedAsterisk>
                        </>
                      }
                      name="CredentialIssuerID"
                      select
                      fullWidth
                      error={touched.CredentialIssuerID && Boolean(errors.CredentialIssuerID)}
                      helperText={touched.CredentialIssuerID && errors.CredentialIssuerID}
                      value={values.CredentialIssuerID}
                      onClick={handleClick}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <>
                            {values.CredentialIssuerID && (
                              <InputAdornment position="end" sx={{ mr: 3 }}>
                                <IconButton
                                  onClick={() => clearIssuerSelection(setFieldValue)}
                                >
                                  <CloseIcon />
                                </IconButton>
                              </InputAdornment>
                            )}
                          </>
                        ),
                      }}
                      onBlur={() => {
                        if (!values.CredentialIssuerID) {
                          setFieldError('CredentialIssuerID', 'Issuer is required');
                        }
                      }}
                    >
                      <MenuItem value="">Select an issuer</MenuItem>
                      {issuers.map((issuer) => (
                        <MenuItem key={issuer.IssuerID} value={issuer.IssuerID}>
                          {`${issuer.IssuerName || ''} - ${issuer.IssuerRepFirstName || ''} ${issuer.IssuerRepLastName || ''} - ${issuer.IssuerRepEmailID || ''}`}
                        </MenuItem>
                      ))}
                    </Field>

                  </Grid>
                  <Grid item xs={3}>
                    <FileInputWrapper>
                      <UploadInput
                        accept="image/*"
                        id="CredentialImageURL"
                        name="CredentialImageURL"
                        type="file"
                        onChange={(event) => {
                          const file = event.currentTarget.files[0];
                          setImageName(file ? file.name : null);
                          setFieldValue('CredentialImageURL', file);
                        }}
                      />
                      <Button variant="outlined" component="span" startIcon={<UploadIcon />}>
                        Upload Credential Logo <RedAsterisk>*</RedAsterisk>
                      </Button>
                    </FileInputWrapper>
                    <UploadText style={{ marginLeft: '8px' }}>
                      {imageName ? (
                        <>
                          {imageName}
                          <IconButton
                            onClick={(event) => {
                              event.stopPropagation();
                              handleClearImage(setFieldValue);
                            }}
                            size="small"
                            color="error"
                            aria-label="clear"
                          >
                            <Clear fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        'No file chosen'
                      )}
                    </UploadText>
                    {errors.CredentialImageURL && (
                      <Typography color="error">{errors.CredentialImageURL}</Typography>
                    )}
                  </Grid>
                  {/* <Grid item xs={6}>
                    <Field
                      as={TextField}
                      label={
                        <>
                          {'Credential GroupName ( Optional )'}
                        </>
                      }
                      name="CredentialGroupName"
                      fullWidth
                      error={touched.CredentialGroupName && Boolean(errors.CredentialGroupName)}
                      helperText={touched.CredentialGroupName && errors.CredentialGroupName}
                    />
                  </Grid> */}
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth
                      disabled={isSubmitting}
                      startIcon={isSubmitting && <CircularProgress size={24} color="inherit" />}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit'}
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            </Card>
            <CustomMenu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              {issuers.map((issuer) => (
                <MenuItem
                  key={issuer.IssuerID}
                  onClick={() => {
                    setFieldValue('CredentialIssuerID', issuer.IssuerID);
                    handleClose();
                  }}
                >
                  {`${issuer.IssuerName || ''} - ${issuer.IssuerRepFirstName || ''} ${issuer.IssuerRepLastName || ''} - ${issuer.IssuerRepEmailID || ''}`}
                </MenuItem>
              ))}
            </CustomMenu>
          </div>
        )}
      </Formik>
    </>
  );
}

export default CredentialForm;
